import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Settings,
  AlertCircle,
  Info,
  AlertTriangle,
  CheckCircle,
  Filter,
  Search,
  RefreshCw
} from 'lucide-react';
import type { RootState, AppDispatch } from '@/store';
import {
  fetchNotifications,
  fetchNotificationSummary,
  markNotificationAsRead,
  markNotificationAsUnread,
  bulkMarkAsRead,
  deleteNotification,
  resetNotifications
} from '@/store/slices/notificationsSlice';
import type { Notification, NotificationType } from '@/types';
import { formatDistanceToNow, format } from 'date-fns';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassInput,
  GlassAlert,
  GlassLoadingSpinner,
  SectionReveal,
} from '@/design-system';

const NotificationsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    notifications, 
    summary, 
    unreadCount, 
    isLoading, 
    error, 
    hasMore 
  } = useSelector((state: RootState) => state.notifications);
  
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<'all' | 'unread' | 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    dispatch(resetNotifications());
    dispatch(fetchNotifications({ unreadOnly: filterType === 'unread', limit: 50 }));
    dispatch(fetchNotificationSummary());
  }, [dispatch, filterType]);

  const handleRefresh = () => {
    dispatch(resetNotifications());
    dispatch(fetchNotifications({ unreadOnly: filterType === 'unread', limit: 50 }));
    dispatch(fetchNotificationSummary());
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      dispatch(fetchNotifications({ 
        unreadOnly: filterType === 'unread', 
        limit: 50,
        page: Math.floor(notifications.length / 50) + 1
      }));
    }
  };

  const handleSelectNotification = (notificationId: string) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    const visibleIds = filteredNotifications.map(n => n.id);
    setSelectedNotifications(
      selectedNotifications.length === visibleIds.length ? [] : visibleIds
    );
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedNotifications.length > 0) {
      await dispatch(bulkMarkAsRead(selectedNotifications));
      setSelectedNotifications([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.length > 0) {
      await Promise.all(
        selectedNotifications.map(id => dispatch(deleteNotification(id)))
      );
      setSelectedNotifications([]);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await dispatch(markNotificationAsRead(notificationId));
  };

  const handleMarkAsUnread = async (notificationId: string) => {
    await dispatch(markNotificationAsUnread(notificationId));
  };

  const handleDelete = async (notificationId: string) => {
    await dispatch(deleteNotification(notificationId));
    setSelectedNotifications(prev => prev.filter(id => id !== notificationId));
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'INFO':
        return <Info className="w-5 h-5 text-accent-blue" />;
      case 'SUCCESS':
        return <CheckCircle className="w-5 h-5 text-status-success" />;
      case 'WARNING':
        return <AlertTriangle className="w-5 h-5 text-status-warning" />;
      case 'ERROR':
        return <AlertCircle className="w-5 h-5 text-status-error" />;
      default:
        return <Info className="w-5 h-5 text-accent-blue" />;
    }
  };

  const getNotificationBadgeColor = (type: NotificationType) => {
    switch (type) {
      case 'INFO':
        return 'bg-status-info-bg text-status-info border-status-info/30';
      case 'SUCCESS':
        return 'bg-status-success-bg text-status-success border-status-success/30';
      case 'WARNING':
        return 'bg-status-warning-bg text-status-warning border-status-warning/30';
      case 'ERROR':
        return 'bg-status-error-bg text-status-error border-status-error/30';
      default:
        return 'bg-status-info-bg text-status-info border-status-info/30';
    }
  };

  // Filter notifications based on search and type
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = searchQuery === '' || 
      notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
      (filterType === 'unread' && !notification.isRead) ||
      notification.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen glass-page-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <SectionReveal>
          <GlassPageHeader
            title="Notifications"
            subtitle="Manage your notifications and preferences"
            icon={<Bell className="w-6 h-6" />}
            iconGradient="cyan"
            className="mb-6"
            actions={
              <div className="flex items-center gap-3">
                <GlassButton
                  onClick={handleRefresh}
                  variant="secondary"
                  size="sm"
                  disabled={isLoading}
                  leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
                >
                  Refresh
                </GlassButton>

                <Link to="/notifications/preferences">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    leftIcon={<Settings className="w-4 h-4" />}
                  >
                    Preferences
                  </GlassButton>
                </Link>
              </div>
            }
          />
        </SectionReveal>

        {/* Summary Cards */}
        {summary && (
          <SectionReveal delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard padding="md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-blue/20 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-accent-blue" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{summary.total}</p>
                    <p className="text-sm text-muted-foreground/60">Total</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard padding="md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-cyan/20 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-accent-cyan" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{summary.unread}</p>
                    <p className="text-sm text-muted-foreground/60">Unread</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard padding="md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-emerald/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-accent-emerald" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{summary.read}</p>
                    <p className="text-sm text-muted-foreground/60">Read</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard padding="md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-amber/20 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-accent-amber" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{summary.byType.WARNING + summary.byType.ERROR}</p>
                    <p className="text-sm text-muted-foreground/60">Alerts</p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </SectionReveal>
        )}

        {/* Filters and Search */}
        <SectionReveal delay={0.15}>
          <GlassCard padding="md">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-muted-foreground/60" />
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterType === 'all'
                      ? 'bg-accent-cyan text-white'
                      : 'bg-surface-glass text-muted-foreground hover:bg-surface-glass-hover'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterType('unread')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterType === 'unread'
                      ? 'bg-accent-cyan text-white'
                      : 'bg-surface-glass text-muted-foreground hover:bg-surface-glass-hover'
                  }`}
                >
                  Unread ({unreadCount})
                </button>
                <button
                  onClick={() => setFilterType('INFO')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterType === 'INFO'
                      ? 'bg-accent-blue text-white'
                      : 'bg-surface-glass text-muted-foreground hover:bg-surface-glass-hover'
                  }`}
                >
                  Info
                </button>
                <button
                  onClick={() => setFilterType('WARNING')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterType === 'WARNING'
                      ? 'bg-accent-amber text-white'
                      : 'bg-surface-glass text-muted-foreground hover:bg-surface-glass-hover'
                  }`}
                >
                  Warnings
                </button>
                <button
                  onClick={() => setFilterType('ERROR')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filterType === 'ERROR'
                      ? 'bg-accent-rose text-white'
                      : 'bg-surface-glass text-muted-foreground hover:bg-surface-glass-hover'
                  }`}
                >
                  Errors
                </button>
              </div>

              <div className="w-64">
                <GlassInput
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(value) => setSearchQuery(value)}
                  leftIcon={<Search className="w-4 h-4" />}
                />
              </div>
            </div>
          </GlassCard>
        </SectionReveal>

        {/* Bulk Actions */}
        {selectedNotifications.length > 0 && (
          <SectionReveal delay={0.2}>
            <GlassCard padding="md">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">
                  {selectedNotifications.length} notification{selectedNotifications.length !== 1 ? 's' : ''} selected
                </span>

                <div className="flex items-center gap-2">
                  <GlassButton
                    onClick={handleBulkMarkAsRead}
                    variant="secondary"
                    size="sm"
                    leftIcon={<CheckCheck className="w-4 h-4" />}
                  >
                    Mark as Read
                  </GlassButton>

                  <GlassButton
                    onClick={handleBulkDelete}
                    variant="ghost"
                    size="sm"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    className="text-accent-rose hover:bg-accent-rose/10"
                  >
                    Delete
                  </GlassButton>
                </div>
              </div>
            </GlassCard>
          </SectionReveal>
        )}

        {/* Notifications List */}
        <SectionReveal delay={0.25}>
          <GlassCard padding="none">
            {error && (
              <div className="p-4 border-b border-border-primary">
                <GlassAlert variant="error" message={error} />
              </div>
            )}

            {isLoading && notifications.length === 0 ? (
              <div className="p-12 flex flex-col items-center justify-center">
                <GlassLoadingSpinner size="lg" variant="gradient" label="Loading notifications..." />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground/60">
                <Bell className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-foreground mb-2">No notifications found</h3>
                <p className="text-sm">
                  {searchQuery ? 'Try adjusting your search terms' : 'You\'re all caught up!'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border-primary">
                {/* Select All Header */}
                <div className="p-4 bg-surface-glass">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.length === filteredNotifications.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-accent-cyan bg-surface-glass border-border-primary rounded focus:ring-accent-cyan focus:ring-2"
                    />
                    <span className="text-sm text-foreground">
                      Select all visible notifications
                    </span>
                  </label>
                </div>

                {/* Notification Items */}
                {filteredNotifications.map((notification) => (
                  <NotificationListItem
                    key={notification.id}
                    notification={notification}
                    isSelected={selectedNotifications.includes(notification.id)}
                    onSelect={handleSelectNotification}
                    onMarkAsRead={handleMarkAsRead}
                    onMarkAsUnread={handleMarkAsUnread}
                    onDelete={handleDelete}
                    getIcon={getNotificationIcon}
                    getBadgeColor={getNotificationBadgeColor}
                  />
                ))}
              </div>
            )}

            {/* Load More */}
            {hasMore && filteredNotifications.length > 0 && (
              <div className="p-4 text-center border-t border-border-primary">
                <GlassButton
                  onClick={handleLoadMore}
                  variant="secondary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </GlassButton>
              </div>
            )}
          </GlassCard>
        </SectionReveal>
      </div>
    </div>
  );
};

interface NotificationListItemProps {
  notification: Notification;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread: (id: string) => void;
  onDelete: (id: string) => void;
  getIcon: (type: NotificationType) => React.ReactNode;
  getBadgeColor: (type: NotificationType) => string;
}

const NotificationListItem: React.FC<NotificationListItemProps> = ({
  notification,
  isSelected,
  onSelect,
  onMarkAsRead,
  onMarkAsUnread,
  onDelete,
  getIcon,
  getBadgeColor,
}) => {
  return (
    <div className={`p-4 hover:bg-surface-glass-hover transition-colors ${
      !notification.isRead ? 'bg-accent-cyan/5' : ''
    }`}>
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(notification.id)}
          className="w-4 h-4 text-accent-cyan bg-surface-glass border-border-primary rounded focus:ring-accent-cyan focus:ring-2 mt-1"
        />

        {/* Icon */}
        <div className="flex-shrink-0 mt-1">
          {getIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`text-sm font-medium ${
                  notification.isRead ? 'text-foreground' : 'text-foreground'
                }`}>
                  {notification.title}
                </h3>

                <GlassBadge
                  variant={
                    notification.type === 'ERROR' ? 'danger' :
                    notification.type === 'WARNING' ? 'warning' :
                    notification.type === 'SUCCESS' ? 'success' : 'info'
                  }
                  size="sm"
                >
                  {notification.type}
                </GlassBadge>

                {!notification.isRead && (
                  <div className="w-2 h-2 bg-accent-cyan rounded-full"></div>
                )}
              </div>

              <p className={`text-sm mb-2 ${
                notification.isRead ? 'text-muted-foreground/60' : 'text-muted-foreground'
              }`}>
                {notification.message}
              </p>

              <div className="flex items-center gap-4 text-xs text-muted-foreground/60">
                <span>
                  {format(new Date(notification.createdAt), 'MMM d, yyyy h:mm a')}
                </span>
                <span>
                  {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                </span>
                {notification.readAt && (
                  <span>
                    Read {formatDistanceToNow(new Date(notification.readAt), { addSuffix: true })}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              {notification.isRead ? (
                <button
                  onClick={() => onMarkAsUnread(notification.id)}
                  className="p-2 hover:bg-surface-glass-hover rounded text-muted-foreground/60 hover:text-foreground transition-colors"
                  title="Mark as unread"
                >
                  <Bell className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => onMarkAsRead(notification.id)}
                  className="p-2 hover:bg-surface-glass-hover rounded text-muted-foreground/60 hover:text-foreground transition-colors"
                  title="Mark as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}

              <button
                onClick={() => onDelete(notification.id)}
                className="p-2 hover:bg-surface-glass-hover rounded text-muted-foreground/60 hover:text-accent-rose transition-colors"
                title="Delete notification"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;